"use client";

import Link from "next/link";
import { useSession } from "next-auth/react";
import { useState } from "react";

import { toast } from "react-toastify";

import { api } from "~/utils/api";
import { Input } from "~/components/v3/themed/Input";
import { Button } from "~/components/v3/themed/Button";
import { Checkbox } from "~/components/shadcn/Checkbox";

import CountryCodeSelector from "~/components/themed/CountryCodeSelector";
import { VerifyPhoneNumber } from "./PhoneNumberVerification";

import {
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "~/components/v3/themed/Dialog";
import { CustomizableLayout } from "~/types/CustomizableLayout";
import { cn } from "~/utils/fontsConfig";

type Props = {
    user: any;
    phoneNumber?: string;
    setShowPhoneModal: (value: boolean) => void;
    handlePhoneSubmit: (phone: string) => void;
    customization?: CustomizableLayout;
};

export const PhoneNumberStep = ({
    user,
    phoneNumber,
    setShowPhoneModal,
    handlePhoneSubmit,
    customization,
}: Props) => {
    const { data: session, update: updateSession } = useSession();

    const [countryCode, setCountryCode] = useState(user?.countryCode ?? "");
    const [phone, setPhone] = useState<string>(phoneNumber ?? "");
    const [checked, setChecked] = useState(false);
    const [showVerification, setShowVerification] = useState(false);

    const upsertPhoneNumber = api.user.upsertPhoneNumber.useMutation();

    const handleUpsertPhoneNumber = async () => {
        const phoneChanged =
            phone !== user?.phone || countryCode !== user?.countryCode;

        const toastId = toast.loading("Checking if verified...");

        await upsertPhoneNumber.mutateAsync(
            { phone, countryCode },
            {
                onSuccess: async () => {
                    if (
                        phoneChanged ||
                        !session?.user.isUserPhoneNumberVerified
                    ) {
                        toast.dismiss(toastId);
                        setShowVerification(true);
                    } else {
                        toast.update(toastId, {
                            render: "Phone number verified!",
                            type: "success",
                            isLoading: false,
                            autoClose: 1300,
                        });

                        handlePhoneSubmit(phone);
                        setShowPhoneModal(false);
                    }

                    await updateSession({
                        ...session,
                        user: {
                            ...session?.user,
                            phone,
                            countryCode,
                            isUserPhoneNumberVerified:
                                !phoneChanged &&
                                session?.user.isUserPhoneNumberVerified,
                        },
                    });
                },
                onError: (error: any) => {
                    console.log("error", error);
                    toast.update(toastId, {
                        render: "Error verifying phone number.",
                        type: "error",
                        isLoading: false,
                        autoClose: 1300,
                    });
                },
            },
        );
    };

    if (showVerification) {
        return (
            <div>
                <DialogHeader>
                    <DialogTitle
                        className={cn(
                            "text-xl font-bold",
                            customization?.labelClassName,
                        )}
                    >
                        Verification Code
                    </DialogTitle>
                </DialogHeader>

                <VerifyPhoneNumber
                    setShowModal={setShowVerification}
                    verifiedCallback={() => {
                        handlePhoneSubmit(phone);
                        setShowPhoneModal(false);
                    }}
                />
            </div>
        );
    }

    return (
        <div>
            <DialogHeader>
                <DialogTitle
                    className={cn(
                        "text-xl font-bold",
                        customization?.labelClassName,
                    )}
                >
                    Enter Phone Number
                </DialogTitle>
                <DialogDescription
                    className={cn(
                        "text-sm font-normal tracking-wide text-[#A3A8B4]",
                        customization?.descriptionClassName,
                    )}
                >
                    Please enter your phone number in the field below.
                </DialogDescription>
            </DialogHeader>

            <div className="mt-2 flex flex-col gap-4 text-white">
                <div className="space-y-2">
                    <label
                        htmlFor="countryCode"
                        className={cn(
                            "text-sm font-medium",
                            customization?.inputLabelClassName,
                        )}
                    >
                        Country Code
                    </label>
                    <CountryCodeSelector
                        countryCode={countryCode}
                        setCountryCode={setCountryCode}
                        customization={customization}
                    />
                </div>

                <div className="space-y-2">
                    <label
                        htmlFor="phone"
                        className={cn(
                            "text-sm font-medium",
                            customization?.inputLabelClassName,
                        )}
                    >
                        Phone Number
                    </label>
                    <Input
                        id="modalPhoneNumber"
                        name="phoneNumber"
                        placeholder="Enter phone number"
                        className={cn(
                            "rounded-[0.626rem] tracking-wide",
                            customization?.inputClassName,
                        )}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                    />
                </div>

                <div className="flex flex-col gap-2 text-sm">
                    <p
                        className={cn(
                            "text-sm font-normal tracking-wide text-[#A3A8B4]",
                            customization?.descriptionClassName,
                        )}
                    >
                        I am opting in to receive SMS notifications from River.
                        Message frequency varies. Message and Data rates may
                        apply. For more info, see our{" "}
                        <Link
                            href="https://www.getriver.io/privacy-policy"
                            className={cn(
                                "underline",
                                customization?.linkClassName,
                            )}
                        >
                            Privacy Policy
                        </Link>{" "}
                        and{" "}
                        <Link
                            href="https://www.getriver.io/terms-and-conditions"
                            className={cn(
                                "underline",
                                customization?.linkClassName,
                            )}
                        >
                            SMS Terms & Conditions
                        </Link>
                        . Text HELP for help. Text STOP to cancel.
                    </p>

                    <div className="mt-2 flex items-center space-x-2">
                        <Checkbox
                            id="terms"
                            checked={checked}
                            className="data-[state=checked]:border-secondary data-[state=checked]:bg-secondary"
                            onCheckedChange={(isChecked) =>
                                setChecked(isChecked as boolean)
                            }
                        />
                        <label
                            htmlFor="terms"
                            className={cn(
                                "text-sm font-medium",
                                customization?.inputLabelClassName,
                            )}
                        >
                            I agree
                        </label>
                    </div>
                    <p
                        className={cn(
                            "mt-2",
                            customization?.descriptionClassName,
                        )}
                    >
                        Just for event updates. No spam.
                    </p>
                </div>

                <div className="flex justify-between">
                    <Button
                        variant="secondary"
                        onClick={() => setShowPhoneModal(false)}
                        className={cn(
                            "bg-white font-american-grotesk text-black hover:border-none hover:bg-whitelabel-freepress-button-primary hover:text-white hover:ring-0",
                            customization?.buttons?.secondary,
                        )}
                    >
                        Skip for now
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleUpsertPhoneNumber}
                        disabled={!checked}
                        className={cn(
                            "bg-whitelabel-freepress-button-primary font-american-grotesk text-white",
                            customization?.buttons?.default,
                        )}
                    >
                        Continue
                    </Button>
                </div>
            </div>
        </div>
    );
};
